import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { UserAuthenticationService } from "@app/authentication/shared/user-authentication.service";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { CerrixRole } from "@enums/authorization/CerrixRole";
import { isGuid } from "@methods/uniqueMethods";
import { TabMenuItem } from "@models/generic/TabModels/TabMenuItem";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { UserMaintenancePermissionModel } from "@models/permissions/UserMaintenancePermissionModel";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { ActiveToast, ToastrService } from "ngx-toastr";
import { finalize } from "rxjs/operators";
import { UserMaintenanceService } from "../services/user-maintenance.service";
import { UserDetailModel } from "../shared/user-detail-model";
import { UserStandingdataModel } from "../shared/user-standingdata-model";
import { UserAuditComponent } from "./user-audit/user-audit.component";
import { UserHistoryComponent } from "./user-history/user-history.component";
import { firstValueFrom } from "rxjs";
import { injectTranslation, provideTranslationScope, TranslatePipe } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/user/i18n/scope";
import { CerrixLoaderComponent } from "@app/shared/components/cerrix-loader/cerrix-loader.component";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { DateTimeFormatPipe } from "@app/shared/pipes/date-time-format.pipe";
import { UserInfoComponent } from "./user-info/user-info.component";
import { UserNotificationSettingsComponent } from "@app/user/shared/notification-settings/user-notification-settings.component";
import { UserRolesComponent } from "./user-roles/user-roles.component";
import { UserLoginsComponent } from "./user-logins/user-logins.component";
import { ApiKeyOverviewComponent } from "@app/user/shared/api-key/components/api-key-overview/api-key-overview.component";
import { DetailOptionsComponent } from "@app/shared/components/detail-options/detail-options.component";

@Component({
    selector: "app-user-detail",
    templateUrl: "./user-detail.component.html",
    styleUrls: ["./user-detail.component.scss"],
    imports: [
        CerrixLoaderComponent,
        MenuSystemDirective,
        TranslatePipe,
        DateTimeFormatPipe,
        UserInfoComponent,
        UserNotificationSettingsComponent,
        UserRolesComponent,
        UserLoginsComponent,
        ApiKeyOverviewComponent,
        UserAuditComponent,
        UserHistoryComponent,
        DetailOptionsComponent,
    ],
    viewProviders: [provideTranslationScope(scope)],
})
export class UserDetailComponent implements OnInit {
    private _ds = inject(UserMaintenanceService);
    private userAuthenticationService = inject(UserAuthenticationService);
    private _toastr = inject(ToastrService);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);

    @ViewChild("audit") auditComp: UserAuditComponent;
    @ViewChild("history") historyComp: UserHistoryComponent;

    tab: TabModel;
    id: string;

    permissions: UserMaintenancePermissionModel;
    user: UserDetailModel;
    sd: UserStandingdataModel;

    isNewUserRequest = false;
    saveToastr: ActiveToast<any>;
    canSave = false;

    hideSaveBar = false;

    constructor() {
        const permService = inject(PermissionsService);

        this.permissions = permService.permissions.UserMaintenance;
        this.canSave =
            this.permissions.canEditDetails ||
            this.permissions.canEditLogins ||
            this.permissions.canAssignGroups ||
            this.permissions.canAssignRoles;
    }

    async ngOnInit() {
        // The tab name and toasts below are translated before any template renders a
        // `transloco` pipe, so preload the scope first.
        await this._translation.ready();

        // Is editor and is requesting existing item or is creator and is requesting new item.
        if (
            (this.id && (!isGuid(this.id) || !this.permissions.hasAccess)) ||
            (!this.id && !this.permissions.canCreate)
        ) {
            this.tab.close(false);
            return;
        }

        this.isNewUserRequest = !this.id;
        const call = this.id
            ? firstValueFrom(this._ds.getUser(this.id))
            : firstValueFrom(this._ds.getNewUser());

        call.then((user) => {
            if (!user) {
                this.tab.close(false);
                this._toastr.warning(this._translation.t("detail.userNotFound"), "");
                return;
            }
            this.loadUser(user);
        });
    }

    loadUser(user: UserDetailModel) {
        this.user = user;
        this.loadSd();

        this.tab.name = this.isNewUserRequest
            ? this._translation.t("detail.tabNameNew")
            : this._translation.t("detail.tabNameExisting", {
                  firstname: user.firstname,
                  lastname: user.lastname,
              });
        this.tab.showLoader = false;
    }

    loadSd() {
        this.sd = {
            organizationPromise: firstValueFrom(this._ds.getOrganizations()),
            roleGroupPromise: this.permissions.canAssignRoles
                ? firstValueFrom(this._ds.getRoleGroups())
                : null,
        };

        this.sd.organizationPromise.then((o) => {
            this.sd.organizations = o;
        });

        if (this.permissions.canAssignRoles) {
            this.sd.roleGroupPromise.then((rg) => {
                this.sd.roleGroups = rg;
            });
        }
    }

    async save() {
        const copyModel: UserDetailModel = JSON.parse(JSON.stringify(this.user));
        const selectedRoles: CerrixRole[] = [];
        copyModel.moduleRoles.forEach((module) => {
            module.roles.forEach((role) => {
                if (role.checked) {
                    selectedRoles.push(role.roleID);
                }
            });
        });

        copyModel.selectedRoles = selectedRoles;
        copyModel.moduleRoles = null;

        if (this.saveToastr) {
            this.saveToastr.portal.destroy();
            this.saveToastr = null;
        }

        if (copyModel.isOwnProfile) {
            const logoutConfirmation = await this._promptService
                .confirm(
                    this._translation.t("detail.updateOwnProfileConfirmTitle"),
                    this._translation.t("detail.updateOwnProfileConfirmMessage")
                )
                .toPromise();

            if (!logoutConfirmation) {
                return;
            }
        }

        const savingPrompt = this._promptService.loader(this._translation.t("detail.savingUser"));

        this._ds
            .storeUser(copyModel)
            .pipe(finalize(() => savingPrompt.close()))
            .subscribe((x) => {
                if (x.stored) {
                    if (copyModel.isOwnProfile) {
                        this._promptService.loader(this._translation.t("detail.loggingOutUser"));
                        this.userAuthenticationService.logout().subscribe(() => {
                            window.location.reload();
                        });
                    } else {
                        this.tab.id = this.id = x.guid;
                        this.tab.refresh();
                        this.saveToastr = this._toastr.success(
                            this._translation.t("detail.userSaved"),
                            this._translation.t("detail.successTitle")
                        );
                    }
                } else {
                    this.saveToastr = this._toastr.warning(
                        x.validations.join("\n"),
                        this._translation.t("detail.saveFailedTitle"),
                        {
                            disableTimeOut: true,
                        }
                    );
                }
            });
    }

    delete() {
        const deletePrompt = this._promptService.loader(
            this._translation.t("detail.deletingUser")
        );
        this._ds
            .deleteUser(this.user.guid)
            .pipe(finalize(() => deletePrompt.close()))
            .subscribe((x) => {
                this._toastr.success(
                    this._translation.t("detail.userDeleted"),
                    this._translation.t("detail.deletedTitle")
                );
                this.tab.parent.refresh();
                this.tab.close(false);
            });
    }

    unlockUser() {
        if (this.user.dateLocked) {
            this._ds.unlockUser(this.user.guid).subscribe((x) => {
                this.user.dateLocked = null;
                this.user.failedLogins = 0;
            });
        }
    }

    handleMenuItemClick(menuItem: TabMenuItem) {
        if (!menuItem) {
            return;
        }

        this.hideSaveBar = menuItem.menuItemId === "apikeytab";
        switch (menuItem.menuItemId) {
            case "roles":
                window.dispatchEvent(new Event("resize"));
                break;
            case "audittab":
                this.auditComp.load();
                break;
            case "historytab":
                this.historyComp.load();
                break;
        }
    }
}
